import { Mail, Package, ShieldCheck, Users, Workflow, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SurfaceCard } from '../../components/ui/SurfaceCard';
import { adminApi, type ApiRecord } from '../../lib/api';

const adminCards = [
	{ title: 'Identity & access', icon: ShieldCheck },
	{ title: 'Mail operations', icon: Mail },
	{ title: 'Catalog & workflow', icon: Package }
];

function AdminSection({
	title,
	queryKey,
	queryFn,
	createFn,
	deleteFn,
	fields,
	records
}: {
	title: string;
	queryKey: string;
	queryFn: () => Promise<ApiRecord[]>;
	createFn: (payload: Record<string, unknown>) => Promise<ApiRecord>;
	deleteFn: (id: number | string) => Promise<void>;
	fields: Array<{ key: string; label: string; type?: 'text' | 'email' | 'password' | 'checkbox' | 'number' }>;
	records?: ApiRecord[];
}) {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<Record<string, string | boolean>>(
		fields.reduce<Record<string, string | boolean>>((accumulator, field) => {
			accumulator[field.key] = field.type === 'checkbox' ? false : '';
			return accumulator;
		}, {})
	);

	const query = useQuery({
		queryKey: ['admin', queryKey],
		queryFn
	});

	const createMutation = useMutation({
		mutationFn: () => createFn(form),
		onSuccess: async () => {
			toast.success(`${title.slice(0, -1)} created`);
			setForm(
				fields.reduce<Record<string, string | boolean>>((accumulator, field) => {
					accumulator[field.key] = field.type === 'checkbox' ? false : '';
					return accumulator;
				}, {})
			);
			await queryClient.invalidateQueries({ queryKey: ['admin', queryKey] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : `Failed to create ${title.toLowerCase()}`);
		}
	});

	const deleteMutation = useMutation({
		mutationFn: deleteFn,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['admin', queryKey] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : `Failed to delete ${title.toLowerCase()}`);
		}
	});

	const rows = records ?? query.data ?? [];

	return (
		<SurfaceCard title={title}>
			<div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
				{fields.map((field) =>
					field.type === 'checkbox' ? (
						<label key={field.key} className="flex items-center gap-2 rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-sm text-secondary">
							<input
								type="checkbox"
								checked={Boolean(form[field.key])}
								onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.checked }))}
								className="rounded border-strong bg-canvas"
							/>
							<span>{field.label}</span>
						</label>
					) : (
						<input
							key={field.key}
							type={field.type ?? 'text'}
							value={String(form[field.key] ?? '')}
							onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
							placeholder={field.label}
							className="rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary"
						/>
					)
				)}
			</div>
			<div className="mt-2 flex justify-end">
				<button onClick={() => createMutation.mutate()} className="btn-primary">
					{createMutation.isPending ? 'Saving...' : `Add ${title.slice(0, -1)}`}
				</button>
			</div>
			<div className="mt-3 overflow-hidden rounded-xl border border-subtle">
				<table className="min-w-full divide-y divide-subtle text-sm">
					<thead className="bg-surface-2 text-left text-muted">
						<tr>
							<th className="px-3 py-2 text-xs font-medium">Record</th>
							<th className="px-3 py-2 text-xs font-medium">Details</th>
							<th className="px-3 py-2 text-xs font-medium text-right">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-subtle">
						{query.isLoading ? (
							<tr>
								<td colSpan={3} className="px-3 py-6 text-center text-sm text-muted">Loading {title.toLowerCase()}...</td>
							</tr>
						) : rows.length > 0 ? (
							rows.map((row, index) => (
								<tr key={String(row.id ?? index)} className="bg-canvas/40">
									<td className="px-3 py-2 text-sm text-primary">{String(row.name ?? row.email ?? row.label ?? row.domain ?? `Record ${index + 1}`)}</td>
									<td className="px-3 py-2 text-sm text-secondary">
										{String(row.username ?? row.from_email ?? row.mailbox_label ?? row.sku ?? row.smtp_host ?? row.title ?? '')}
									</td>
									<td className="px-3 py-2 text-right">
										<button
											onClick={() => {
												if (row.id == null) {
													return;
												}
												deleteMutation.mutate(String(row.id));
											}}
											className="rounded-lg border border-danger/30 bg-danger/10 px-2 py-1 text-xs font-medium text-danger"
										>
											Delete
										</button>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan={3} className="px-3 py-6 text-center text-sm text-muted">No records yet.</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</SurfaceCard>
	);
}

export function AdminPage() {
	return (
		<div className="space-y-3">
			<section className="rounded-2xl border border-subtle bg-gradient-to-br from-surface to-surface-2 px-5 py-3 shadow-panel backdrop-blur-xl">
				<h1 className="text-2xl font-semibold text-primary">Admin center</h1>
			</section>
			<div className="grid gap-3 lg:grid-cols-3">
				{adminCards.map((card) => {
					const Icon = card.icon;
					return (
						<div key={card.title} className="rounded-2xl border border-subtle bg-surface-2/80 p-3 shadow-panel">
							<div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-contrast">
								<Icon className="h-4 w-4" />
							</div>
							<h2 className="mt-3 text-base font-semibold text-primary">{card.title}</h2>
						</div>
					);
				})}
			</div>
			<div className="grid gap-3 xl:grid-cols-2">
				<AdminSection
					title="Users"
					queryKey="users"
					queryFn={adminApi.users}
					createFn={(payload) =>
						adminApi.createUser({
							name: payload.name,
							email: payload.email,
							password: payload.password,
							is_admin: payload.is_admin
						})
					}
					deleteFn={adminApi.deleteUser}
					fields={[
						{ key: 'name', label: 'Full name' },
						{ key: 'email', label: 'Email', type: 'email' },
						{ key: 'password', label: 'Password', type: 'password' },
						{ key: 'is_admin', label: 'Administrator', type: 'checkbox' }
					]}
				/>
				<AdminSection
					title="Mailboxes"
					queryKey="mailboxes"
					queryFn={adminApi.mailboxes}
					createFn={(payload) =>
						adminApi.createMailbox({
							label: payload.label,
							imap_host: payload.imap_host,
							username: payload.username,
							protocol: payload.protocol || 'imap',
							poll_seconds: Number(payload.poll_seconds || 300),
							enabled: payload.enabled
						})
					}
					deleteFn={adminApi.deleteMailbox}
					fields={[
						{ key: 'label', label: 'Label' },
						{ key: 'imap_host', label: 'IMAP host' },
						{ key: 'username', label: 'Username' },
						{ key: 'protocol', label: 'Protocol' },
						{ key: 'poll_seconds', label: 'Poll seconds', type: 'number' },
						{ key: 'enabled', label: 'Enabled', type: 'checkbox' }
					]}
				/>
				<AdminSection
					title="Domains"
					queryKey="domains"
					queryFn={adminApi.domains}
					createFn={(payload) => adminApi.createDomain({ domain: payload.domain, mailbox_label: payload.mailbox_label || null })}
					deleteFn={adminApi.deleteDomain}
					fields={[
						{ key: 'domain', label: 'Domain' },
						{ key: 'mailbox_label', label: 'Mailbox label' }
					]}
				/>
				<AdminSection
					title="SMTP Profiles"
					queryKey="smtp-profiles"
					queryFn={adminApi.smtpProfiles}
					createFn={(payload) =>
						adminApi.createSMTPProfile({
							domain: payload.domain,
							smtp_host: payload.smtp_host,
							username: payload.username,
							password: payload.password,
							from_name: payload.from_name,
							from_email: payload.from_email,
							use_tls: payload.use_tls,
							enabled: payload.enabled
						})
					}
					deleteFn={adminApi.deleteSMTPProfile}
					fields={[
						{ key: 'domain', label: 'Domain' },
						{ key: 'smtp_host', label: 'SMTP host' },
						{ key: 'username', label: 'Username' },
						{ key: 'password', label: 'Password', type: 'password' },
						{ key: 'from_name', label: 'From name' },
						{ key: 'from_email', label: 'From email', type: 'email' },
						{ key: 'use_tls', label: 'Use TLS', type: 'checkbox' },
						{ key: 'enabled', label: 'Enabled', type: 'checkbox' }
					]}
				/>
				<AdminSection
					title="Vendors"
					queryKey="vendors"
					queryFn={adminApi.vendors}
					createFn={(payload) => adminApi.createVendor({ name: payload.name, domain: payload.domain || null })}
					deleteFn={adminApi.deleteVendor}
					fields={[
						{ key: 'name', label: 'Vendor name' },
						{ key: 'domain', label: 'Vendor domain' }
					]}
				/>
				<AdminSection
					title="Products"
					queryKey="products"
					queryFn={adminApi.products}
					createFn={(payload) =>
						adminApi.createProduct({
							vendor_id: Number(payload.vendor_id),
							name: payload.name,
							sku: payload.sku || null,
							active: payload.active
						})
					}
					deleteFn={adminApi.deleteProduct}
					fields={[
						{ key: 'vendor_id', label: 'Vendor ID', type: 'number' },
						{ key: 'name', label: 'Product name' },
						{ key: 'sku', label: 'SKU' },
						{ key: 'active', label: 'Active', type: 'checkbox' }
					]}
				/>
			</div>
			<SurfaceCard title="Production readiness notes">
				<div className="grid gap-2 md:grid-cols-2">
					<div className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-xs leading-5 text-secondary">Core CRM collections now support live listing, creation, and deletion through the Go API.</div>
					<div className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-xs leading-5 text-secondary">Admin surfaces are now wired for local users, mailboxes, SMTP profiles, domains, vendors, and products.</div>
					<div className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-xs leading-5 text-secondary">OAuth providers are mounted on the backend and ready for frontend management next.</div>
					<div className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-xs leading-5 text-secondary">Email threads, global search, and richer edit flows still need deeper implementation.</div>
				</div>
			</SurfaceCard>
		</div>
	);
}
